/**
 * Performance Matrix data — lifted out of SupplierPerformanceMatrixView so the
 * page's "Export to Excel" can export the matrix it is showing (Customer review
 * 11 Sep, review item 18: in Matrix view the button exported the table).
 *
 * The request is the year + the header view scope + the page filters (same
 * param names the Table sends). It refetches on the WHOLE request: the old
 * effect listed four filters by hand and missed the Supplier Group, Company
 * Site and Live/Historical switches, so changing any of those in Matrix view
 * left the previous grid on screen.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { suppliersAPI, PerformanceMatrixRow, MatrixRatingBand } from '../../../lib/api';
import { useView } from '../../../contexts/ViewContext';
import { getErrorMessage } from '../../../hooks/useFetchData';

export const CALENDAR_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export type MatrixParams = Record<string, string | number | boolean>;

/** The page filters the matrix honours (CR-6312 / CR-7264). */
export interface MatrixFilterParams {
  is_preferred?: boolean;
  warehouse?: string;
  exclude_na_ratings?: boolean;
  company_id?: string;
  /** CR-7264: Supplier Group (a parent group includes its child groups' suppliers) */
  company__group?: string;
  /** CR-7264 rework: Company Site — narrows within the header view scope */
  site?: string;
  /** CR-7336: Commodity Group / Type — matched on each line's (supplier, customer) relationship */
  commodity_group?: string;
  commodity_type?: string;
  /** W3-T9: 'live' reads today's open-overdue arrears; omitted = frozen month-end snapshots */
  data_mode?: 'historical' | 'live';
}

export interface PerformanceMatrixState {
  rows: PerformanceMatrixRow[];
  total: number;
  bands: MatrixRatingBand[];
  monthKeys: readonly string[];
  yearLabel: string;
  year: number;
  setYear: (updater: (y: number) => number) => void;
  loading: boolean;
  error: string | null;
  /** The request the grid was fetched with, minus the year (for per-site rows). */
  requestParams: MatrixParams;
  /** CR-6312: a legend band narrows the grid to rows holding that grade. */
  selectedBand: string | null;
  setSelectedBand: (grade: string | null) => void;
  /** The rows on screen (after the band filter and the quick search) — what the export writes. */
  visibleRows: PerformanceMatrixRow[];
  /** CR-7297: the page's quick search, as applied to the grid ('' = none). */
  search: string;
  rowHoldsGrade: (row: PerformanceMatrixRow, grade: string) => boolean;
}

/**
 * Fetch the matrix for the current view + filters while `enabled`.
 *
 * @param filterParams - the page's filters, Table param names
 * @param enabled - only while the Matrix view is on screen
 * @param search - the page's quick search (supplier name); narrows the grid client-side
 * @returns the grid, its scale and the year controls
 */
export function usePerformanceMatrix(
  filterParams: MatrixFilterParams,
  enabled: boolean,
  search = '',
): PerformanceMatrixState {
  const { getViewQueryParams } = useView();
  const [year, setYearState] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<PerformanceMatrixRow[]>([]);
  const [total, setTotal] = useState(0);
  const [bands, setBands] = useState<MatrixRatingBand[]>([]);
  // CR-5883: month order + year label come from the backend (fiscal start).
  const [monthKeys, setMonthKeys] = useState<readonly string[]>(CALENDAR_MONTHS);
  const [yearLabel, setYearLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const viewParams = getViewQueryParams();
  const requestParams = useMemo<MatrixParams>(() => {
    const params: MatrixParams = {};
    Object.entries(viewParams).forEach(([k, v]) => {
      if (v !== undefined) params[k] = v;
    });
    if (filterParams.is_preferred) params['is_preferred'] = true;
    if (filterParams.warehouse && filterParams.warehouse !== 'all') params['warehouse'] = filterParams.warehouse;
    if (filterParams.exclude_na_ratings) params['exclude_na_ratings'] = 'true';
    // CR-6506: only suppliers that have passed onboarding, as the Table asks —
    // whatever the Exclude N/A checkbox says.
    params['approved_only'] = 'true';
    if (filterParams.data_mode === 'live') params['data_mode'] = 'live';
    if (filterParams.company_id && filterParams.company_id !== 'all') params['company_id'] = filterParams.company_id;
    if (filterParams.company__group && filterParams.company__group !== 'all') {
      params['company__group'] = filterParams.company__group;
    }
    if (filterParams.site && filterParams.site !== 'all') params['site'] = filterParams.site;
    // CR-7336: the matrix was the one view of the page the commodity
    // selection never reached.
    if (filterParams.commodity_group && filterParams.commodity_group !== 'all') {
      params['commodity_group'] = filterParams.commodity_group;
    }
    if (filterParams.commodity_type && filterParams.commodity_type !== 'all') {
      params['commodity_type'] = filterParams.commodity_type;
    }
    return params;
    // Keyed on values, not object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(viewParams), JSON.stringify(filterParams)]);
  const requestKey = JSON.stringify([year, requestParams]);

  const seq = useRef(0);
  useEffect(() => {
    if (!enabled) return;
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    suppliersAPI.getPerformanceMatrix({ year, ...requestParams }).then((res) => {
      if (mine !== seq.current) return;
      setRows(res.data.rows);
      setTotal(res.data.total);
      setBands(res.data.rating_bands ?? []);
      setMonthKeys(res.data.month_keys && res.data.month_keys.length === 12 ? res.data.month_keys : CALENDAR_MONTHS);
      setYearLabel(res.data.year_label ?? String(res.data.year ?? year));
      // CR-5458: the backend falls back to the latest year with data; follow it.
      if (res.data.year && res.data.year !== year) setYearState(res.data.year);
      setLoading(false);
    }).catch((err: unknown) => {
      if (mine !== seq.current) return;
      setError(getErrorMessage(err, 'Failed to load the performance matrix'));
      setLoading(false);
    });
    // requestKey carries year + requestParams by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, requestKey]);

  // CR-6312: a band matches a supplier holding that grade in the Overall cell
  // OR any monthly cell — counting Overall alone left the A chip claiming "no
  // suppliers rated A" while A month cells rendered directly below it.
  const [selectedBand, setSelectedBand] = useState<string | null>(null);
  const rowHoldsGrade = useMemo(
    () => (r: PerformanceMatrixRow, grade: string): boolean =>
      r.OverallRating === grade ||
      monthKeys.some((m) => r[m as keyof PerformanceMatrixRow] === grade),
    [monthKeys],
  );
  // CR-7297: the quick search box sits above both views but only ever reached
  // the Table's request, so in Matrix view typing a name changed nothing — and
  // the export, which writes the rows on screen, could not honour it either.
  // The matrix endpoint takes no search term; the grid is one row per
  // supplier, so it narrows here on the supplier name (the Table searches
  // company name server-side).
  const term = search.trim().toLowerCase();
  const visibleRows = useMemo(
    () => rows.filter((r) =>
      (!selectedBand || rowHoldsGrade(r, selectedBand))
      && (!term || r.Supplier.toLowerCase().includes(term))),
    [rows, selectedBand, rowHoldsGrade, term],
  );
  // Clear a band selection that no longer matches anything (year/filter change).
  useEffect(() => {
    if (selectedBand && !rows.some((r) => rowHoldsGrade(r, selectedBand))) setSelectedBand(null);
  }, [rows, selectedBand, rowHoldsGrade]);

  return {
    rows, total, bands, monthKeys, yearLabel, year,
    setYear: (updater) => setYearState(updater),
    loading, error, requestParams,
    selectedBand, setSelectedBand, visibleRows, rowHoldsGrade, search: term,
  };
}
